/*
 * HLTH home hero with product tabs: behaviour for sections/hlth-home-hero.liquid (1.9.1 item 4, 22 Oct 2026).
 *
 * Video (the UK hero's speed fix): nothing is fetched until the window load event, so the poster, fonts and
 * first-screen images get the connection; phones (under 750px) get the phone MP4 setting when it is filled in,
 * otherwise the adaptive stream (hls.js for browsers without native HLS), with the MP4 as the fallback.
 *
 * Tabs (laptops): click, or the arrow, Home and End keys. A tab shows its panel, swaps the media (the video, or its
 * photo) and pauses the video while a photo shows. The photos get their src after the page has loaded, or on the
 * first tap, and never on a phone. Phones always show the first panel (the CSS holds that too).
 */
(function () {
  var PHONE = '(max-width: 749px)';
  var HLS_JS = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.15/dist/hls.min.js';

  function isPhone() {
    return !!(window.matchMedia && window.matchMedia(PHONE).matches);
  }

  function init(section) {
    if (section.__hhh) return;
    section.__hhh = true;

    var video = section.querySelector('.hhh-video');
    var poster = section.querySelector('.hhh-poster');
    var content = section.querySelector('.hhh-content');
    var photos = Array.prototype.slice.call(section.querySelectorAll('[data-hhh-photo]'));
    var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-hhh-tab]'));
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-hhh-panel]'));
    var current = panels.length ? panels[0].getAttribute('data-hhh-panel') : null;
    var photosLoaded = false;

    function panelById(id) {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-hhh-panel') === id) return panels[i];
      }
      return null;
    }

    function mediaOf(id) {
      var panel = panelById(id);
      return panel ? panel.getAttribute('data-hhh-media') : 'video';
    }

    /* ---- Video ---- */
    function tryPlay() {
      // No source until after load: play() on an empty video would retire the first-touch fallback below.
      if (!video || !video.paused || !(video.currentSrc || video.src)) return;
      if (current && mediaOf(current) !== 'video') return;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    function startVideo() {
      if (!video) return;
      var hlsUrl = video.getAttribute('data-hls-src');
      var mp4Url = video.getAttribute('data-mp4-src');
      var mp4SmUrl = video.getAttribute('data-mp4-sm-src');
      var nativeHls = !!video.canPlayType && !!video.canPlayType('application/vnd.apple.mpegurl');

      function useSrc(url) {
        video.src = url;
        video.preload = 'auto';
        video.addEventListener('loadedmetadata', tryPlay, { once: true });
        video.addEventListener('canplay', tryPlay, { once: true });
        video.load();
        tryPlay();
      }
      function fallbackToMp4() {
        if (mp4Url) useSrc(mp4Url);
      }

      if (isPhone() && mp4SmUrl) return useSrc(mp4SmUrl);
      if (hlsUrl && nativeHls) return useSrc(hlsUrl);
      if (!hlsUrl) return fallbackToMp4();
      var script = document.createElement('script');
      script.src = HLS_JS;
      script.async = true;
      script.onload = function () {
        if (window.Hls && window.Hls.isSupported()) {
          var hls = new window.Hls({ enableWorker: true, maxBufferLength: 10 });
          hls.loadSource(hlsUrl);
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, tryPlay);
          hls.on(window.Hls.Events.ERROR, function (e, data) {
            if (data && data.fatal) {
              try { hls.destroy(); } catch (_) {}
              fallbackToMp4();
            }
          });
        } else {
          fallbackToMp4();
        }
      };
      script.onerror = fallbackToMp4;
      document.head.appendChild(script);
    }

    if (video) {
      if (poster) {
        video.addEventListener('playing', function () { poster.style.opacity = '0'; }, { once: true });
      }
      // iOS blocks autoplay in Low Power Mode: start on the first touch, scroll or click instead.
      var kick = function () {
        tryPlay();
        if (video && !video.paused) {
          document.removeEventListener('touchstart', kick, true);
          document.removeEventListener('click', kick, true);
          document.removeEventListener('scroll', kick, true);
        }
      };
      document.addEventListener('touchstart', kick, { capture: true, passive: true });
      document.addEventListener('click', kick, true);
      document.addEventListener('scroll', kick, { capture: true, passive: true });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') tryPlay();
      });
    }

    /* ---- Photos: after load or on the first tap, laptops only ---- */
    function loadPhotos() {
      if (photosLoaded || !photos.length || isPhone()) return;
      photosLoaded = true;
      photos.forEach(function (img) {
        if (img.getAttribute('data-srcset')) img.setAttribute('srcset', img.getAttribute('data-srcset'));
        if (img.getAttribute('data-src')) img.setAttribute('src', img.getAttribute('data-src'));
      });
    }

    /* ---- Tabs ---- */
    function show(id, focus) {
      if (!panelById(id)) return;
      current = id;
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-hhh-tab') === id;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on && focus) tab.focus();
      });
      panels.forEach(function (panel) {
        var on = panel.getAttribute('data-hhh-panel') === id;
        panel.classList.toggle('is-active', on);
        if (on) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });
      var media = mediaOf(id);
      if (media !== 'video') loadPhotos();
      [video, poster].forEach(function (el) {
        if (el) el.classList.toggle('is-off', media !== 'video');
      });
      photos.forEach(function (img) {
        img.classList.toggle('is-on', img.getAttribute('data-hhh-photo') === media);
      });
      if (video) {
        if (media === 'video') tryPlay();
        else video.pause();
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        show(tab.getAttribute('data-hhh-tab'));
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        show(tabs[next].getAttribute('data-hhh-tab'), true);
      });
    });

    // A phone shows the first panel, so a laptop narrowed to phone width goes back to it.
    if (window.matchMedia && panels.length) {
      var mq = window.matchMedia(PHONE);
      var onChange = function () {
        if (mq.matches) show(panels[0].getAttribute('data-hhh-panel'));
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    function afterLoad() {
      startVideo();
      loadPhotos();
    }
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });

    /* ---- Repaint nudge (from hlth-hero-v2) ----
       After long video sessions Chromium can fail to repaint the content layer when the tab returns or the hero
       scrolls back into view; a zero-change transform toggle makes it re-raster. */
    function heal() {
      if (!content) return;
      content.style.transform = 'translateZ(0.0001px)';
      requestAnimationFrame(function () { content.style.transform = ''; });
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') heal();
    });
    if ('IntersectionObserver' in window) {
      var lastVisible = true;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !lastVisible) heal();
          lastVisible = entry.isIntersecting;
        });
      }, { threshold: 0.1 }).observe(section);
    }
  }

  function scan(scope) {
    if (scope.matches && scope.matches('[data-hhh]')) init(scope);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-hhh]'), init);
  }
  scan(document);
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
